import http from '@frontend/api/http';
import { HYPATIA_URL } from '@frontend/envConfig';
import type { AgentGrant, AgentVisibility, ResourcePermissions } from '@frontend/types/agent';

import { AgentSortField, AgentStatus, AgentType, SortOrder } from '../hooks/useAgentsUrlParams';

export type UnifiedAgent = {
  id: string;
  agent_id: string;
  name: string;
  description: string;
  version: string;
  agent_type: 'specialized' | 'workflow';
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  is_active: boolean;
  is_deleted: boolean;
  is_installed: boolean;
  is_system_agent?: boolean;
  conversation_mode?: boolean;
  permissions?: ResourcePermissions;
  visibility?: AgentVisibility;
  grants?: AgentGrant[];
  links: {
    self: string;
    collection: string;
    specialized_detail?: string;
    workflow_detail?: string;
    execute?: string;
    executions: string;
  };
};

export type AgentsApiResponse = {
  items: UnifiedAgent[];
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
  links: {
    self: string;
    next?: string;
    previous?: string;
  };
};

export type GetAgentsParams = {
  instanceId: string;
  page?: number;
  pageSize?: number;
  searchQuery?: string;
  agentType?: AgentType;
  status?: AgentStatus;
  sort?: AgentSortField;
  order?: SortOrder;
  createdBy?: string[];
  updatedBy?: string[];
  visibility?: AgentVisibility;
};

export function getAgentsWithPagination({
  instanceId,
  page = 1,
  pageSize = 20,
  searchQuery = '',
  agentType,
  status,
  sort,
  order,
  createdBy,
  updatedBy,
  visibility,
}: GetAgentsParams) {
  return {
    key: [
      'agents',
      instanceId,
      page,
      pageSize,
      searchQuery,
      agentType,
      status,
      sort,
      order,
      createdBy,
      updatedBy,
      visibility,
    ] as const,
    queryFn: async (): Promise<AgentsApiResponse> => {
      // The UI uses the literal 'optimizely' inside `createdBy`/`updatedBy` to
      // mean "agents installed from the Optimizely Agent Directory". Split it
      // back out into the dedicated `include_system_agents` query param.
      const includeSystemAgents =
        !!createdBy?.includes('optimizely') || !!updatedBy?.includes('optimizely');
      const creatorIds = createdBy?.filter(id => id !== 'optimizely') ?? [];
      const updaterIds = updatedBy?.filter(id => id !== 'optimizely') ?? [];

      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        status: status || 'all',
        ...(searchQuery && { search_query: searchQuery }),
        ...(agentType && { agent_type: agentType }),
        ...(sort && { sort }),
        ...(order && { order }),
        ...(creatorIds.length && { created_by: creatorIds.join(',') }),
        ...(includeSystemAgents && { include_system_agents: 'true' }),
        ...(updaterIds.length && { updated_by: updaterIds.join(',') }),
        ...(visibility && { visibility }),
      });

      const res = await http.get<AgentsApiResponse>(
        `${HYPATIA_URL}/api/v1/${instanceId}/agents?${params}`
      );
      return res.data;
    },
  };
}

export function getAgentById(instanceId: string, agentId: string) {
  return {
    key: ['agent', instanceId, agentId] as const,
    queryFn: async (): Promise<UnifiedAgent> => {
      const res = await http.get<UnifiedAgent>(
        `${HYPATIA_URL}/api/v1/${instanceId}/agents/${agentId}`
      );
      return res.data;
    },
  };
}

export type ReferencedByWorkflow = {
  id: string;
  name: string;
  is_active: boolean;
  created_by: string | null;
};

export type ReferencedByResponse = {
  count: number;
  workflows: ReferencedByWorkflow[];
};

export function getReferencedByWorkflows(instanceId: string, agentId: string) {
  return {
    key: ['agent-referenced-by', instanceId, agentId] as const,
    queryFn: async (): Promise<ReferencedByResponse> => {
      const res = await http.get<ReferencedByResponse>(
        `${HYPATIA_URL}/api/v1/${instanceId}/agents/${agentId}/referenced-by`
      );
      return res.data;
    },
  };
}

export type AgentShareRequest = {
  id: string;
  status: string;
  requested_by: string;
  resource_id: string;
  resource_name: string;
};

export function getAgentShareRequest(
  instanceId: string,
  agentType: 'specialized' | 'workflow',
  agentId: string
) {
  return {
    key: ['agent-share-request', instanceId, agentType, agentId] as const,
    queryFn: async (): Promise<{ pending_share_request: AgentShareRequest | null }> => {
      const res = await http.get<{ pending_share_request: AgentShareRequest | null }>(
        `${HYPATIA_URL}/api/v1/${instanceId}/agents/${agentType}/${agentId}/share-requests`
      );
      return res.data;
    },
  };
}

function shareRequestActionUrl(
  instanceId: string,
  agentType: 'specialized' | 'workflow',
  agentId: string,
  requestId: string,
  action: 'approve' | 'reject'
) {
  return `${HYPATIA_URL}/api/v1/${instanceId}/agents/${agentType}/${agentId}/share-requests/${requestId}/${action}`;
}

export async function approveAgentShareRequest(
  instanceId: string,
  agentType: 'specialized' | 'workflow',
  agentId: string,
  requestId: string
): Promise<void> {
  await http.post(shareRequestActionUrl(instanceId, agentType, agentId, requestId, 'approve'), {});
}

export async function rejectAgentShareRequest(
  instanceId: string,
  agentType: 'specialized' | 'workflow',
  agentId: string,
  requestId: string,
  rejectionReason?: string
): Promise<void> {
  await http.post(shareRequestActionUrl(instanceId, agentType, agentId, requestId, 'reject'), {
    rejection_reason: rejectionReason ?? '',
  });
}

export function getAgentUnshareRequest(
  instanceId: string,
  agentType: 'specialized' | 'workflow',
  agentId: string
) {
  return {
    key: ['agent-unshare-request', instanceId, agentType, agentId] as const,
    queryFn: async (): Promise<{ pending_unshare_request: AgentShareRequest | null }> => {
      const res = await http.get<{ pending_unshare_request: AgentShareRequest | null }>(
        `${HYPATIA_URL}/api/v1/${instanceId}/agents/${agentType}/${agentId}/unshare-requests`
      );
      return res.data;
    },
  };
}

export async function createAgentUnshareRequest(
  instanceId: string,
  agentType: 'specialized' | 'workflow',
  agentId: string
): Promise<AgentShareRequest> {
  const res = await http.post<AgentShareRequest>(
    `${HYPATIA_URL}/api/v1/${instanceId}/agents/${agentType}/${agentId}/unshare-requests`,
    {}
  );
  return res.data;
}

function unshareRequestActionUrl(
  instanceId: string,
  agentType: 'specialized' | 'workflow',
  agentId: string,
  requestId: string,
  action: 'approve' | 'reject' | 'cancel'
) {
  return `${HYPATIA_URL}/api/v1/${instanceId}/agents/${agentType}/${agentId}/unshare-requests/${requestId}/${action}`;
}

export async function approveAgentUnshareRequest(
  instanceId: string,
  agentType: 'specialized' | 'workflow',
  agentId: string,
  requestId: string
): Promise<void> {
  await http.post(
    unshareRequestActionUrl(instanceId, agentType, agentId, requestId, 'approve'),
    {}
  );
}

export async function rejectAgentUnshareRequest(
  instanceId: string,
  agentType: 'specialized' | 'workflow',
  agentId: string,
  requestId: string,
  rejectionReason?: string
): Promise<void> {
  await http.post(unshareRequestActionUrl(instanceId, agentType, agentId, requestId, 'reject'), {
    rejection_reason: rejectionReason ?? '',
  });
}

export type UsedInWorkflow = {
  id: string;
  agent_id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string | null;
  created_by: string | null;
};

export function getUsedInWorkflows(instanceId: string, agentId: string) {
  return {
    key: ['agent-used-in-workflows', instanceId, agentId] as const,
    queryFn: async (): Promise<UsedInWorkflow[]> => {
      const res = await http.get<{ workflows: UsedInWorkflow[] }>(
        `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}/used-in-workflows`
      );
      return res.data.workflows;
    },
  };
}
